import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  CheckIcon,
  ChevronDownIcon,
  CloudDownloadIcon,
  PlusIcon,
} from "@lucide/icons";

import type { OpenAPI } from "simplydo/interfaces";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Button } from "simplydo/react/ui/button";
import {
  Card,
  CardContent,
  CardGroup,
  CardHeader,
  CardMeta,
} from "simplydo/react/ui/card";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type {
  ProjectBoardImportLanesResponse,
  ProjectBoardSetupChallengesResponse,
} from "./types";

type IProjectBoardSetup = {
  forId: string;
  lanes: OpenAPI.Schemas["ProjectLane"][];
  dataLoaded?: boolean;
  setLanes: (lanes: OpenAPI.Schemas["ProjectLane"][]) => void;
  setupModalOpen: boolean;
  setSetupModalOpen: Dispatch<SetStateAction<boolean>>;
  laneFilter?: string[];
  canManageChallenges?: boolean;
};

const ProjectBoardSetup = ({
  forId,
  lanes = [],
  dataLoaded,
  setLanes,
  setupModalOpen,
  setSetupModalOpen,
  laneFilter,
  canManageChallenges,
}: IProjectBoardSetup) => {
  // We use this flag to ensure the modal doesn't reopen once it's been closed
  const [setupHandled, setSetupHandled] = useState<boolean>(false);
  const [myChallenges, setMyChallenges] = useState<
    OpenAPI.Schemas["Challenge"][]
  >([]);
  const [importing, setImporting] = useState<boolean>(false);
  const [selectedChallengeId, setSelectedChallengeId] = useState<string>("");
  const [accordionOpen, setAccordionOpen] = useState<boolean>(false);
  const user = useAppSelector((state) => state.user);
  const userId = user?._id;

  const { t } = useTranslation();

  const getChallenges = useCallback(() => {
    if (!userId) return;
    api.users.getChallenges(
      userId,
      { forType: "projectBoard", query: "" },
      (data: ProjectBoardSetupChallengesResponse) => {
        setMyChallenges(data.challenges);
      },
      () => {},
    );
  }, [userId]);

  const importLanes = useCallback(() => {
    setImporting(true);
    api.boards.importProjectLanes(
      forId,
      { fromBoardId: selectedChallengeId },
      (data: ProjectBoardImportLanesResponse) => {
        if (data.success) {
          setLanes(data.lanes);
          toast.success("Lanes imported successfully.");
          setImporting(false);
          setSetupModalOpen(false);
        } else {
          toast.error("Failed to import lanes.");
        }
        setImporting(false);
      },
      (err) => {
        setImporting(false);
        toast.error(err.message);
      },
    );
  }, [forId, selectedChallengeId, setLanes, setSetupModalOpen]);

  useEffect(() => {
    getChallenges();
  }, [getChallenges]);

  useEffect(() => {
    if (
      (canManageChallenges ||
        util.hasPermission(user, "challenge.editProjectBoard", forId)) &&
      !lanes.length &&
      dataLoaded &&
      !setupHandled &&
      !laneFilter?.length
    ) {
      setSetupModalOpen(true);
      setSetupHandled(true);
    }
  }, [
    lanes,
    dataLoaded,
    setSetupModalOpen,
    setupHandled,
    laneFilter?.length,
    canManageChallenges,
    forId,
    user,
  ]);

  return (
    <Modal open={setupModalOpen} onClose={() => setSetupModalOpen(false)}>
      <ModalHeader>Welcome to the {t("generic.idea")} board</ModalHeader>
      <ModalContent>
        <p>
          This {t("generic.idea")} board is not setup yet. You may import the
          lane structure from another {t("generic.challenge")} you manage, or
          start from scratch.
        </p>
        <Accordion>
          <AccordionTitle
            active={accordionOpen}
            onClick={() => setAccordionOpen((prevOpen) => !prevOpen)}
          >
            Select {t("generic.challengeWithArticle")} to import from
          </AccordionTitle>
          <AccordionContent active={accordionOpen}>
            <CardGroup itemsPerRow={4}>
              {myChallenges.map((c) => (
                <Card
                  key={c._id}
                  onClick={() =>
                    setSelectedChallengeId((prevSelected) =>
                      prevSelected === c._id ? "" : c._id,
                    )
                  }
                >
                  <Button
                    color={selectedChallengeId === c._id ? "violet" : undefined}
                    style={{ position: "absolute", top: 5, left: 5, width: 50 }}
                  >
                    <Icon icon={CheckIcon} />
                  </Button>
                  <div
                    style={{
                      height: 150,
                      backgroundPosition: "center center",
                      backgroundSize: "cover",
                      backgroundImage: `url(${util.mixinCssUrlFallback(util.challengeImage(c), util.challengeImage())})`,
                    }}
                  />
                  <CardContent>
                    <CardHeader>{c.name}</CardHeader>
                    <CardMeta>
                      Copy the lane structure from this {t("generic.challenge")}
                    </CardMeta>
                    <CardMeta style={{ marginTop: 10 }}>
                      <Link
                        className="with-border"
                        style={{ textDecorationLine: "underline" }}
                        to={`/challenges/${c._id}/board`}
                        target="_blank"
                      >
                        View {t("generic.idea")} board
                      </Link>
                    </CardMeta>
                  </CardContent>
                </Card>
              ))}
            </CardGroup>
          </AccordionContent>
        </Accordion>
      </ModalContent>
      <ModalActions>
        {selectedChallengeId ? (
          <Button
            color="green"
            loading={importing}
            onClick={() => importLanes()}
          >
            <Icon icon={CloudDownloadIcon} />
            {`Import from selected ${t("generic.challenge")}`}
          </Button>
        ) : (
          <Button onClick={() => setSetupModalOpen(false)}>
            <Icon icon={PlusIcon} />
            Start from scratch
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
};

export default ProjectBoardSetup;
